import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ui, statusStyle } from "@/lib/ui";
import { Plus, Globe, RefreshCw, Loader2, Search, FileUp, X, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { DateRangeFilter } from "@/components/DateRangeFilter";

export default function MyWebsites() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regen, setRegen] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [range, setRange] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkItems, setBulkItems] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/sites").then(({ data }) => setSites(data))
      .catch(() => toast.error("Failed to load websites")).finally(() => setLoading(false));
  }, []);

  const pollRegen = () => {
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get("/sites/regenerate-all/status");
        setRegen(data);
        if (data.status !== "running") {
          clearInterval(iv);
          if (data.status === "completed") {
            toast.success(`Regenerated ${data.ok}/${data.total} site(s)${data.failed ? `, ${data.failed} failed` : ""}`);
            api.get("/sites").then(({ data }) => setSites(data)).catch(() => {});
          }
        }
      } catch { /* ignore transient */ }
    }, 3000);
  };

  useEffect(() => {
    api.get("/sites/regenerate-all/status").then(({ data }) => {
      setRegen(data);
      if (data.status === "running") pollRegen();
    }).catch(() => {});
  }, []); // eslint-disable-line

  const regenerateAll = async () => {
    try {
      const { data } = await api.post("/sites/regenerate-all");
      toast.success(`Regenerating ${data.total} site(s) — this can take a minute`);
      setRegen({ status: "running", done: 0, total: data.total, ok: 0, failed: 0 });
      pollRegen();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to start"); }
  };

  const categories = useMemo(
    () => Array.from(new Set(sites.map((s) => s.category_name).filter(Boolean))).sort(),
    [sites]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const from = range?.from || null;
    const to = range?.to || null;
    return sites.filter((s) => {
      if (q && !(
        (s.name || "").toLowerCase().includes(q) ||
        (s.domain || "").toLowerCase().includes(q) ||
        (s.custom_domain || "").toLowerCase().includes(q)
      )) return false;
      if (category && s.category_name !== category) return false;
      if (from || to) {
        const u = new Date(s.updated_at);
        if (from && u < from) return false;
        if (to && u > to) return false;
      }
      return true;
    });
  }, [sites, search, category, range]);

  const anyFilter = search || category || range;

  useEffect(() => { setPage(1); }, [search, category, range, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const pageStart = (curPage - 1) * pageSize;
  const paged = filtered.slice(pageStart, pageStart + pageSize);

  const onBulkFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    Promise.all(files.map((f) => new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res({ filename: f.name, content: String(r.result || "") });
      r.readAsText(f);
    }))).then((list) => {
      setBulkItems(list.map((it) => {
        const match = sites.find((s) => s.gsc_file?.name === it.filename);
        return { ...it, site_id: match?.id || "" };
      }));
    });
    e.target.value = "";
  };

  const GSC_RE = /^google[a-zA-Z0-9]+\.html$/;

  const submitBulk = async () => {
    const items = bulkItems.filter((i) => i.site_id);
    if (!items.length) { toast.error("Assign at least one file to a site"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/sites/gsc-bulk-upload", {
        items: items.map(({ site_id, filename, content }) => ({ site_id, filename, content })),
      });
      const redeployed = data.results.filter((r) => r.redeployed).length;
      const skipped = bulkItems.length - items.length;
      toast.success(`Uploaded ${data.ok}/${data.total} file(s)${redeployed ? `, ${redeployed} redeployed` : ""}${skipped ? `, ${skipped} skipped` : ""}`);
      setShowBulk(false);
      setBulkItems([]);
      api.get("/sites").then(({ data }) => setSites(data)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk upload failed"); }
    finally { setBulkBusy(false); }
  };

  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className={ui.pageHeader}>My Websites</h1>
          <p className={`${ui.muted} mt-1`}>Every site you've created, with status and last update.</p>
          <p className="text-xs text-slate-400 mt-1 font-mono" data-testid="sites-total-count">
            {loading ? "Loading…" : `${sites.length} website${sites.length === 1 ? "" : "s"} generated${anyFilter ? ` · ${filtered.length} matching` : ""}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="bulk-gsc-btn" onClick={() => setShowBulk(true)} className={ui.btnGhost}>
            <FileUp className="w-4 h-4 text-emerald-400" /> Bulk GSC Upload
          </button>
          <button data-testid="regenerate-all-btn" onClick={regenerateAll} disabled={regen?.status === "running"} className={ui.btnGhost}>
            {regen?.status === "running" ? <Loader2 className="w-4 h-4 spin" /> : <RefreshCw className="w-4 h-4 text-indigo-400" />}
            {regen?.status === "running" ? `Regenerating ${regen.done}/${regen.total}…` : "Regenerate All"}
          </button>
          <Link to="/create" data-testid="new-website-btn" className={ui.btnPrimary}><Plus className="w-4 h-4" /> New Website</Link>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="site-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by business or domain…"
            className={`${ui.input} pl-9`}
          />
        </div>
        <div>
          <label className="block text-[11px] text-slate-500 mb-1 uppercase tracking-wider font-mono">Category</label>
          <select data-testid="category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className={`${ui.input} max-w-[13rem]`}>
            <option value="">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-slate-500 mb-1 uppercase tracking-wider font-mono">Updated</label>
          <DateRangeFilter testid="sites-date-range" value={range} onChange={setRange} />
        </div>
        {anyFilter && (
          <button data-testid="clear-filters" onClick={() => { setSearch(""); setCategory(""); setRange(null); }} className="text-xs text-slate-400 hover:text-slate-200 mb-2.5">Clear filters</button>
        )}
      </div>

      <div className={`${ui.card} overflow-hidden`}>
        <table className="w-full" data-testid="sites-table">
          <thead>
            <tr>
              {["Business", "Category", "City", "Domain", "Status", "Updated"].map((h) => (
                <th key={h} className="bg-slate-950/80 px-4 py-3 text-left text-xs font-mono tracking-wider text-slate-400 font-semibold uppercase border-b border-slate-800">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.map((s) => (
              <tr key={s.id} data-testid={`site-row-${s.id}`} onClick={() => navigate(s.site_type === "national" ? `/national/${s.id}` : `/websites/${s.id}`)}
                className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors cursor-pointer">
                <td className="px-4 py-3.5 text-sm text-slate-100 font-medium">{s.name}{s.site_type === "national" && <span className={`${ui.badge} ml-2 bg-indigo-950/60 border-indigo-500/40 text-indigo-300`}>National</span>}</td>
                <td className="px-4 py-3.5 text-sm text-slate-300">{s.category_name}</td>
                <td className="px-4 py-3.5 text-sm text-slate-300">{s.site_type === "national" ? (s.national?.market || "National") : `${s.business?.city}, ${s.business?.state}`}</td>
                <td className="px-4 py-3.5 text-sm text-slate-400">
                  {(s.domain || s.intended_domain) ? <span className="inline-flex items-center gap-1.5 text-emerald-400"><Globe className="w-3.5 h-3.5" />{s.domain || s.intended_domain}</span> : <span className="text-slate-600">—</span>}
                </td>
                <td className="px-4 py-3.5"><span className={`${ui.badge} ${statusStyle[s.status] || statusStyle.draft}`}>{s.status}</span></td>
                <td className="px-4 py-3.5 text-sm text-slate-500 font-mono">{new Date(s.updated_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {!loading && sites.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-16 text-center text-slate-500">
                No websites yet. <Link to="/create" className="text-indigo-400 hover:underline">Create your first one.</Link>
              </td></tr>
            )}
            {!loading && sites.length > 0 && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-16 text-center text-slate-500" data-testid="no-search-results">
                No websites match your filters.
              </td></tr>
            )}
          </tbody>
        </table>
        {filtered.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-800 text-sm text-slate-400" data-testid="sites-pagination">
            <div className="flex items-center gap-2">
              <span>Show</span>
              <select data-testid="page-size-select" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className={`${ui.input} w-auto py-1 text-sm`}>
                {[10, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>per page</span>
            </div>
            <div className="flex items-center gap-3">
              <span data-testid="pagination-range">{pageStart + 1}–{Math.min(pageStart + pageSize, filtered.length)} of {filtered.length}</span>
              <button data-testid="pagination-prev" disabled={curPage <= 1} onClick={() => setPage(curPage - 1)} className="p-1.5 rounded border border-slate-700 disabled:opacity-40 hover:bg-slate-800"><ChevronLeft className="w-4 h-4" /></button>
              <span data-testid="pagination-page">Page {curPage} / {totalPages}</span>
              <button data-testid="pagination-next" disabled={curPage >= totalPages} onClick={() => setPage(curPage + 1)} className="p-1.5 rounded border border-slate-700 disabled:opacity-40 hover:bg-slate-800"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      {showBulk && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 backdrop-blur-sm py-10 px-4" data-testid="bulk-gsc-modal">
          <div className={`${ui.card} w-full max-w-2xl p-6`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h2 className={ui.sectionHeader}>Bulk GSC Upload</h2>
                <p className={`${ui.muted} mt-1 text-sm`}>Upload multiple <code>google….html</code> files and assign each to its site. Deployed sites are redeployed automatically so Google can verify them.</p>
              </div>
              <button data-testid="bulk-gsc-close" onClick={() => { setShowBulk(false); setBulkItems([]); }} className="p-1.5 text-slate-400 hover:text-slate-200"><X className="w-5 h-5" /></button>
            </div>

            <label className="inline-flex items-center gap-1.5 cursor-pointer mt-3">
              <input type="file" accept=".html,text/html" multiple onChange={onBulkFiles} className="hidden" data-testid="bulk-gsc-file" />
              <span className={ui.btnGhost}><FileUp className="w-4 h-4 text-emerald-400" /> Choose verification files</span>
            </label>

            {bulkItems.length > 0 && (
              <div className="mt-4 space-y-2 max-h-[45vh] overflow-y-auto pr-1" data-testid="bulk-gsc-list">
                {bulkItems.map((it, idx) => {
                  const valid = GSC_RE.test(it.filename);
                  return (
                    <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border border-slate-800 bg-slate-900/40">
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-mono truncate ${valid ? "text-slate-200" : "text-rose-400"}`}>{it.filename}</p>
                        {!valid && <p className="text-[11px] text-rose-400">Expected a google&lt;code&gt;.html file</p>}
                      </div>
                      <select
                        data-testid={`bulk-gsc-select-${idx}`}
                        value={it.site_id}
                        onChange={(e) => setBulkItems((prev) => prev.map((p, i) => i === idx ? { ...p, site_id: e.target.value } : p))}
                        className={`${ui.input} max-w-[16rem] text-sm py-1.5`}
                      >
                        <option value="">— assign to site —</option>
                        {sites.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}{s.domain ? ` · ${s.domain}` : ""}</option>
                        ))}
                      </select>
                      <button onClick={() => setBulkItems((prev) => prev.filter((_, i) => i !== idx))} className="p-1 text-slate-500 hover:text-slate-300"><X className="w-4 h-4" /></button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-slate-800">
              <button onClick={() => { setShowBulk(false); setBulkItems([]); }} className="text-sm text-slate-400 hover:text-slate-200">Cancel</button>
              <button data-testid="bulk-gsc-submit" onClick={submitBulk} disabled={bulkBusy || !bulkItems.some((i) => i.site_id)} className={ui.btnPrimary}>
                {bulkBusy ? <Loader2 className="w-4 h-4 spin" /> : <Check className="w-4 h-4" />} Upload &amp; Redeploy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
